import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Breadcrumbs,
  Button,
  Content,
  Loading,
} from '@orobix/orobix-fe-components'
import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'

import useDriverStyle from '../../components/Driver/driver.styles'
import PageHeader from '../../components/PageHeader/PageHeader'
import { SettingsContext } from '../../components/Settings'
import useUpdateSettings from '../../components/Settings/useUpdateSettings'
import {
  closeRunStatusSocket,
  createRunsStatusSocket,
} from '../../store/sockets'
import { useDispatch, useStore } from '../../store/store'
import { fetchProduct, fetchProject } from '../../store/storeActions'
import { vars } from '../../utils/appConfig'
import { locations } from '../../utils/locations'
import { isProductDisabled } from '../../utils/productTypes'
import getDriver from './Driver'
import TrainingDetails from './TrainingDetails'
import TrainingsAside from './TrainingsAside'
import TrainingsList from './TrainingsList'
import useStyles from './TrainingsRoot.styles'

interface RouteParams {
  productId: string
  projectId: string
}

interface LocationState {
  trainingId: number
}

const TrainingsRoot = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const history = useHistory()
  const { product, project } = useStore()
  const dispatch = useDispatch()
  const { productId, projectId } = useParams<RouteParams>()
  const location = useLocation<LocationState>()
  const [selectedTrainingId, setSelectedTraining] = useState<number>()
  const [detailsRefreshTrigger, setDetailsRefreshTrigger] = useState(0)
  const guidedTourShown = useRef(false)

  useEffect(() => {
    if (location.hash) {
      history.replace({
        hash: location.hash,
        state: {
          trainingId: Number(location.hash.substring(1)),
        },
      })
    }
  }, [history, location.hash])

  useEffect(() => {
    setSelectedTraining(location.state?.trainingId)
  }, [location.state])

  useEffect(() => {
    if (!project) {
      fetchProject(dispatch, Number(projectId))
    }
  }, [dispatch, project, projectId])

  useEffect(() => {
    fetchProduct(dispatch, Number(productId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const socket = createRunsStatusSocket(dispatch)

    return () => {
      closeRunStatusSocket(socket)
    }
  }, [dispatch])

  const { settings } = useContext(SettingsContext)
  const { updateSettings } = useUpdateSettings()

  const updatePreference = () => {
    const checkbox = document.getElementById(
      'doNotShowAgainCheckbox'
    ) as HTMLInputElement
    if (checkbox.checked === settings.preference__show_guided_tour) {
      updateSettings({
        preference__show_guided_tour: !checkbox.checked,
      })
    }
  }

  const classesDriver = useDriverStyle()

  const driverObj = getDriver(
    classesDriver,
    settings,
    updateSettings,
    updatePreference
  )

  useEffect(() => {
    if (guidedTourShown.current === false) {
      if (settings.preference__show_guided_tour && !driverObj.isActive()) {
        driverObj.drive()
        guidedTourShown.current = true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.preference__show_guided_tour])

  return product && project ? (
    <Content
      aside={<TrainingsAside />}
      asideWidth={vars.asideWidth}
      childrenClassName={classes.content}
      breadcrumbs={
        <Breadcrumbs
          elements={[
            <Link to={locations.projects}>{t('Projects')}</Link>,
            <Link to={locations.products(Number(projectId))}>
              {project.name}
            </Link>,
            product.name,
          ]}
        />
      }
    >
      <div className={classes.trainings}>
        <div className={classes.list}>
          <PageHeader
            title={t('Trainings')}
            right={
              <div className={classes.flex}>
                <div id="newTrainingButtonId">
                  <Button
                    onClick={() =>
                      history.push(
                        locations.trainingNew(
                          Number(projectId),
                          Number(productId)
                        )
                      )
                    }
                    label={t('New training')}
                    buttonType="button"
                    type="action"
                    size="md"
                    disabled={isProductDisabled(product.type)}
                    title={
                      isProductDisabled(product?.type)
                        ? t(
                            `You can't start a new training with the current version of the software`
                          )
                        : undefined
                    }
                  />
                </div>
                <div className={classes.marginRightSm}>
                  <Button
                    label={t('Explain me how')}
                    hideLabel
                    icon={<FontAwesomeIcon icon={faCircleQuestion} size="lg" />}
                    type="blank-default"
                    onClick={() => {
                      driverObj.drive()
                    }}
                  />
                </div>
              </div>
            }
          />

          <TrainingsList
            selectedTrainingId={selectedTrainingId}
            setDetailsRefreshTrigger={setDetailsRefreshTrigger}
          />
        </div>

        {selectedTrainingId && (
          <div className={classes.sidePanel}>
            <TrainingDetails
              trainingId={selectedTrainingId}
              detailsRefreshTrigger={detailsRefreshTrigger}
            />
          </div>
        )}
      </div>
    </Content>
  ) : (
    <Content>
      <Loading />
    </Content>
  )
}

export default TrainingsRoot
